import type {TranslationFunction} from 'src/types/language-support';
import type {IEvent} from '../types/IEvent';


export const downloadFile = (file: File, fileName: string) => {
  const url = window.URL.createObjectURL(new Blob([file]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${fileName}.ics` || 'event.ics');
  document.body?.appendChild(link);
  link.click();

  // Cleanup
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const getRsvpStatus = (
  eventDetails?: IEvent,
  attendeeId?: string,
  owner?: string,
): string => {
  let rsvpStatus: string | undefined;
  if (owner === 'HOST') {
    rsvpStatus = eventDetails?.interviewers?.find(
      (interviewer) => interviewer.id === attendeeId,
    )?.status;
  } else {
    rsvpStatus = eventDetails?.candidates?.find(
      (candidate) => candidate.id === attendeeId,
    )?.status;
  }
  return rsvpStatus || 'no';
};

export const getErrorMessage = (
  errorEnum: keyof ReturnType<typeof getErrorMap>,
  t: TranslationFunction,
) => {
  const errorMap = getErrorMap(t);
  return (
    errorMap[errorEnum] || t('Something_went_wrong', 'Something went wrong')
  );
};

export const getErrorMap = (t: TranslationFunction) => ({
  ResourceNotFound: t('Resource_not_found', 'Resource not found'),
  EventNotFound: t('Event_not_found', 'Event not found'),
  EventInThePast: t(
    'This_Event_has_already_occurred',
    'This Event has already occurred',
  ),
  EventAlreadyCancelled: t(
    'Event_has_been_cancelled_already',
    'Event has been cancelled already',
  ),
  OnlyHostAllowed: t(
    'Only_hosts_can_change_the_event',
    'Only hosts can change the event',
  ),
  MinCancellationNoticePassed: t(
    'You_have_exceeded_the_minimum_cancellation_notice',
    'You have exceeded the minimum cancellation notice',
  ),
  AgentNotFound: t('This_User_does_not_exist', 'This User does not exist'),
  MissingAttendeeId: t('Attendee_ID_is_required', 'Attendee ID is required'),
  InternalError: t('Internal_Server_Error', 'Internal Server Error'),
  AccountNotFound: t(
    'Account_Not_Found_for_Agent',
    'Account Not Found for Agent',
  ),
  AlternateHostsNotFound: t(
    'Alternate_Hosts_Not_Found',
    'Alternate Hosts Not Found',
  ),
  ParticipantNotFound: t('Participant_Not_Found', 'Participant Not Found'),
  InvalidNumber: t(
    'Answer_must_be_a_valid_number',
    'Answer must be a valid number',
  ),
  InvalidPhoneNumber: t(
    'Answer_must_be_a_valid_phone_number',
    'Answer must be a valid phone number',
  ),
  InvalidEmailAddress: t(
    'Answer_must_be_a_valid_email_address',
    'Answer must be a valid email address',
  ),
  InvalidChoice: t(
    'Answer_must_be_one_of_the_provided_choices',
    'Answer must be one of the provided choices',
  ),
  BooleanExpected: t('Answer_must_be_a_boolean', 'Answer must be a boolean'),
  RequiredField: t('This_is_a_required_field', 'This is a required field'),
  InvalidTimeRange: t('The_time_range_is_invalid', 'The time range is invalid'),
  UnavailableTimeSlot: t(
    'The_selected_time_slot_is_unavailable_Please_choose_a_different_slot',
    'The selected time slot is unavailable. Please choose a different slot',
  ),
  SameTemplateEventsFound: t(
    'A_similar_event_already_exists_Please_pick_a_different_date',
    'A similar event already exists. Please pick a different date',
  ),
  PhoneNumberInvalid: t(
    'Please_enter_a_valid_phone_number',
    'Please enter a valid phone number',
  ),
  EmailInvalid: t(
    'Please_enter_a_valid_email_address',
    'Please enter a valid email address',
  ),
  InvalidQuestionAnswer: t(
    'One_or_more_answers_provided_are_invalid_Please_review_your_responses_and_try_again',
    'One or more answers provided are invalid. Please review your responses and try again',
  ),
  TimeSlotNotAvailable: t(
    'The_time_slot_you_selected_is_no_longer_available_Please_choose_a_different_slot',
    'The time slot you selected is no longer available. Please choose a different slot',
  ),
  TimeSlotAlreadyBooked: t(
    'The_selected_time_slot_has_already_been_booked_Please_choose_a_different_slot',
    'The selected time slot has already been booked. Please choose a different slot',
  ),
  UnknownError: t(
    'An_unknown_error_occurred_Please_try_again_later',
    'An unknown error occurred. Please try again later',
  ),
  EventTemplateNotFound: t(
    'Event_template_not_found',
    'Event template not found',
  ),
  EventRescheduleLimitReached: t(
    'You_have_exceeded_the_maximum_number_of_reschedules_allowed_for_this_event',
    'You have exceeded the maximum number of reschedules allowed for this event',
  ),
});
